import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, SimpleGrid, UnstyledButton } from '@mantine/core';
import clsx from 'clsx';

import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { comparePeriods, PeriodValue } from './periodRange';

import calendarStyles from '../datepicker/calendar.module.scss';
import datePickerStyles from '../datepicker/date-picker.module.scss';
import styles from './period-picker.module.scss';

type PeriodPickerDropdownProps = {
  years: number[];
  periodsPerYear: number;
  gridColumns: number;
  currentPeriod: PeriodValue;
  formatGridPeriod: (period: number) => string;
  arrowPosition?: 'start' | 'end';
  isUnavailable: (period: PeriodValue) => boolean;
  isSelected: (period: PeriodValue) => boolean;
  isOppositeSelected?: (period: PeriodValue) => boolean;
  isInRange?: (period: PeriodValue) => boolean;
  onPageEarlier: () => void;
  onPageLater: () => void;
  onSelectPeriod: (period: PeriodValue) => void;
  footer?: ReactNode;
};

export function PeriodPickerDropdown({
  years,
  periodsPerYear,
  gridColumns,
  currentPeriod,
  formatGridPeriod,
  arrowPosition = 'start',
  isUnavailable,
  isSelected,
  isOppositeSelected,
  isInRange,
  onPageEarlier,
  onPageLater,
  onSelectPeriod,
  footer,
}: PeriodPickerDropdownProps) {
  const { t } = useTranslation();

  return (
    <Combobox.Dropdown>
      <GoogleFontIcon
        name="arrow_drop_up"
        solid
        data-position={arrowPosition}
        className={datePickerStyles['dropdown-arrow']}
      />
      <Combobox.Options>
        <div className={styles['header']}>
          <UnstyledButton
            className={clsx(
              styles['header-control'],
              calendarStyles['headerControl']
            )}
            onClick={onPageEarlier}
            aria-label={t('app.periodRangePicker.earlier')}
          >
            <GoogleFontIcon name="chevron_left" />
          </UnstyledButton>
          <span>
            {years[0]} — {years.at(-1)}
          </span>
          <UnstyledButton
            className={clsx(
              styles['header-control'],
              calendarStyles['headerControl']
            )}
            onClick={onPageLater}
            aria-label={t('app.periodRangePicker.later')}
          >
            <GoogleFontIcon name="chevron_right" />
          </UnstyledButton>
        </div>
        <div className={styles['grid']}>
          {years.map((year) => (
            <div className={styles['year-row']} key={year}>
              <span className={styles['year-label']}>{year}</span>
              <SimpleGrid cols={gridColumns} spacing={2}>
                {Array.from(
                  { length: periodsPerYear },
                  (_, index) => index + 1
                ).map((periodNumber) => {
                  const period = { year, period: periodNumber };
                  const unavailable = isUnavailable(period);
                  const current = comparePeriods(period, currentPeriod) === 0;
                  return (
                    <UnstyledButton
                      key={periodNumber}
                      className={clsx(
                        styles['period-button'],
                        calendarStyles['day']
                      )}
                      disabled={unavailable}
                      data-unselectable={unavailable || undefined}
                      aria-label={`${formatGridPeriod(periodNumber)} ${year}`}
                      data-selected={isSelected(period) ? true : undefined}
                      data-selector={
                        isOppositeSelected?.(period) ? true : undefined
                      }
                      data-in-range={isInRange?.(period) ? true : undefined}
                      data-today={current || undefined}
                      onClick={() => onSelectPeriod(period)}
                    >
                      {formatGridPeriod(periodNumber)}
                    </UnstyledButton>
                  );
                })}
              </SimpleGrid>
            </div>
          ))}
        </div>
        {footer}
      </Combobox.Options>
    </Combobox.Dropdown>
  );
}
